/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AsyncResult, Customer, ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, ObjectExtensions, Random } from '@msdyn365-commerce-modules/retail-actions';
import { Button, IModuleProps, isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React from 'react';

import { getCatalogId } from '@msdyn365-commerce/core';
import { ProductComparisonItem, ProductComparisonState } from '../product-comparison-state';
import { IProductComparisonButtonProps } from './product-comparison-button.props.autogenerated';

/**
 * Represents product comparison view props.
 */
export interface IProductComparisonButtonViewProps extends IProductComparisonButtonProps<{}> {
    moduleProps: IModuleProps;
    productComparisonButton?: React.ReactNode;
    productComparisonPreview?: React.ReactNode;
}

/**
 * Represents the data which is required for the product comparison button to be rendered.
 */
export interface IProductComparisonButtonData {
    accountInformation?: AsyncResult<Customer>;
}

/**
 * Extended props for the module including info about the product.
 */
export interface IProductComparisonButtonFullProps extends IProductComparisonButtonProps<IProductComparisonButtonData> {
    product: ProductSearchResult | SimpleProduct;
    catalogId: number;
}

/**
 * Extended props for the module including info about the product.
 */
export interface IProductComparisonButtonState {
    canProductBeAddedToProductComparison: boolean;
    currentProductsCount: number;
    comparisonItemId: string;
}

/**
 * Represents the component which renders product comparison table.
 */
export class ProductComparisonButton extends React.PureComponent<IProductComparisonButtonFullProps, IProductComparisonButtonState> {
    private static _productComparisonPreview?: React.ReactNode;

    private readonly _className: string;

    private readonly _instanceId: string;

    private readonly _shouldUsePreview: boolean;

    private static _renderProductComparisonPreview(props: IProductComparisonButtonFullProps): React.ReactNode {
        if (!ProductComparisonButton._productComparisonPreview && ArrayExtensions.hasElements(props.slots.productComparisonPreview)) {
            ProductComparisonButton._productComparisonPreview = props.slots.productComparisonPreview[0];
        }

        return ProductComparisonButton._productComparisonPreview;
    }

    private static _renderProductComparisonButton(
        className: string,
        buttonText: string | undefined,
        title: string,
        onClickHandler?: () => void
    ): React.ReactNode {
        const isDisabled = ObjectExtensions.isNullOrUndefined(onClickHandler);
        let content: React.ReactNode;
        if (buttonText) {
            content = (
                <>
                    <div className={`${className}__icon`} />
                    <div className={`${className}__text`}>{buttonText}</div>
                </>
            );
        }
        return (
            <Button className={className} title={title} onClick={onClickHandler} disabled={isDisabled}>
                {content}
            </Button>
        );
    }

    public constructor(props: IProductComparisonButtonFullProps) {
        super(props);

        this._className = 'ms-product-comparison-button';
        this._instanceId = `${this._className}-${this.props.id}-${Random.Guid.generateGuid()}`;

        this.state = this._getNewState();

        this._shouldUsePreview = !ProductComparisonButton._productComparisonPreview;
        ProductComparisonButton._renderProductComparisonPreview(this.props);
    }

    public componentDidMount(): void {
        ProductComparisonState.instance(this.props.context.request, this.props.data.accountInformation?.result).listModifiedEvent.subscribe(
            {
                instanceId: this._instanceId,

                /**
                 * Updates the state in case the state for the particular product was changed.
                 */
                handler: () => {
                    this.setState(this._getNewState());
                }
            }
        );
    }

    public getSnapshotBeforeUpdate(previousProps: Readonly<IProductComparisonButtonFullProps>): void {
        if (previousProps.product.RecordId !== this.props.product.RecordId || previousProps.catalogId !== this.props.catalogId) {
            this.setState(this._getNewState());
        }
    }

    public componentWillUnmount(): void {
        ProductComparisonState.instance(
            this.props.context.request,
            this.props.data.accountInformation?.result
        ).listModifiedEvent.unsubscribe(this._instanceId);
    }

    public render(): React.ReactNode | null {
        const isConsentGiven = this.props.context.request.cookies.isConsentGiven();
        if (!isConsentGiven) {
            return null;
        }

        const viewProps: IProductComparisonButtonViewProps = {
            ...this.props,
            moduleProps: {
                moduleProps: this.props,
                className: this._className
            }
        };

        if (this._shouldUsePreview) {
            viewProps.productComparisonPreview = ProductComparisonButton._productComparisonPreview;
        }

        const buttonClassName = this.props.config.shouldDisplayText ? `${this._className}__button` : `${this._className}__icon`;

        if (!this.state.canProductBeAddedToProductComparison) {
            const buttonText = this.props.config.shouldDisplayText ? this.props.resources.removeFromComparisonButtonText : undefined;
            viewProps.productComparisonButton = ProductComparisonButton._renderProductComparisonButton(
                classnames(`${buttonClassName}__removing`, buttonClassName),
                buttonText,
                this.props.resources.removeFromComparisonButtonText,
                this._removeFromProductComparison
            );
            return this.props.renderView(viewProps);
        }

        const productsLimit = this._getProductsLimit();
        if (!ObjectExtensions.isNullOrUndefined(productsLimit) && productsLimit <= this.state.currentProductsCount) {
            const buttonText = this.props.config.shouldDisplayText ? this.props.resources.addToComparisonLimitReachedButtonText : undefined;
            viewProps.productComparisonButton = ProductComparisonButton._renderProductComparisonButton(
                buttonClassName,
                buttonText,
                this.props.resources.addToComparisonLimitReachedButtonText
            );
            return this.props.renderView(viewProps);
        }

        const buttonText = this.props.config.shouldDisplayText ? this.props.resources.addToComparisonButtonText : undefined;
        viewProps.productComparisonButton = ProductComparisonButton._renderProductComparisonButton(
            buttonClassName,
            buttonText,
            this.props.resources.addToComparisonButtonText,
            this._addToProductComparison
        );
        return this.props.renderView(viewProps);
    }

    private _getProductsLimit(): number | undefined | null {
        const viewport = isMobile({ variant: VariantType.Browser, context: this.props.context.request });
        if (viewport === 'xs' || viewport === 'sm') {
            return this.props.context.app.config.productComparisonMobileProductsLimit;
        }
        return this.props.context.app.config.productComparisonDesktopProductsLimit;
    }

    /**
     * Adds current product to a product comparison list.
     */
    private readonly _addToProductComparison: () => void = () => {
        const item = new ProductComparisonItem(this.props.product.RecordId, this.props.catalogId);
        ProductComparisonState.instance(this.props.context.request, this.props.data.accountInformation?.result).addItem(item);
    };

    /**
     * Removes current product from the product comparison list where it was added earlier.
     */
    private readonly _removeFromProductComparison: () => void = () => {
        ProductComparisonState.instance(this.props.context.request, this.props.data.accountInformation?.result).removeItemById(
            this.state.comparisonItemId
        );
    };

    private _getProdutComparisonDataByCatalogId(): ProductComparisonItem[] {
        const items = ProductComparisonState.instance(this.props.context.request, this.props.data.accountInformation?.result).items;
        const catalogId = getCatalogId(this.props.context.request);
        const productByCatalogId = items?.filter(product => product.catalogId === catalogId) ?? [];
        return productByCatalogId;
    }

    /**
     * Retrieves the state of the component based on the comparison data.
     * @returns State object for the component.
     */
    private readonly _getNewState: () => IProductComparisonButtonState = () => {
        const comparisonItemId = ProductComparisonItem.getItemId(this.props.product.RecordId, this.props.catalogId);
        const products = this._getProdutComparisonDataByCatalogId();
        return {
            comparisonItemId,
            canProductBeAddedToProductComparison: !ProductComparisonState.instance(
                this.props.context.request,
                this.props.data.accountInformation?.result
            ).hasItemById(comparisonItemId),
            currentProductsCount: products.length
        };
    };
}

export default ProductComparisonButton;
